import API from "helpers/api";
import { Apis } from "services/api";
import { getAPIResponse } from "helpers/getApiResponse";

// Helper function to build department IDs query string
const buildDepartmentQuery = (siteDepartmentId = "", siteDepartmentIds = []) => {
	if (siteDepartmentIds && siteDepartmentIds.length > 0) {
		const query = siteDepartmentIds.map(id => `siteDepartmentIds=${id}`).join('&');
		return query ? `&${query}` : "";
	} else if (siteDepartmentId) {
		return `&siteDepartmentId=${siteDepartmentId ?? ""}`;
	}
	return "";
};

const getDefectRiskRatings = async () => {
	try {
		let response = await API.get(`${Apis.DefectRiskRatings}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectTypes = async () => {
	try {
		let response = await API.get(`${Apis.DefectTypes}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getSiteDepartments = async (siteAppId) => {
	try {
		let response = await API.get(
			`${Apis.SiteDepartments}?siteAppId=${siteAppId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getModelsPublished = async ({ siteDepartmentId = "", siteDepartmentIds = [] }) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		// Remove leading '&' if it exists since this is the first parameter
		const queryString = deptQuery ? deptQuery.substring(1) : "";
		let response = await API.get(
			`${Apis.ModelsPublished}${queryString ? `?${queryString}` : ""}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectsByType = async ({
	defectTypeId = "",
	startDate = "",
	endDate = "",
	defectRiskRatingId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsByType?defectTypeId=${defectTypeId}&startDate=${startDate}&endDate=${endDate}&defectRiskRatingId=${defectRiskRatingId}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectsByRiskRating = async ({
	defectRiskRatingId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsByRiskRating?defectRiskRatingId=${defectRiskRatingId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectsBySystem = async ({
	defectSystemId = "",
	defectRiskRatingId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsBySystem?defectSystemId=${defectSystemId}&defectRiskRatingId=${defectRiskRatingId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectsRegistered = async ({
	defectTypeId = "",
	defectRiskRatingId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	defectStatusType = "",
	notificationRaised = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsRegistered?defectTypeId=${defectTypeId}&defectRiskRatingId=${defectRiskRatingId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}&defectStatusType=${defectStatusType}&notificationRaised=${notificationRaised}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getMissingPartsTools = async ({
	missingPartToolId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/missingPartsTools?missingPartToolId=${missingPartToolId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getOverdueServices = async ({
	roleId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/overdueServices?roleId=${roleId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getPlannedWorkCompliance = async ({
	status = "",
	roleId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/plannedWorkCompliance?status=${status}&roleId=${roleId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getPausesByReason = async ({
	pauseId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/pausesByReason?pauseId=${pauseId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getSkippedTasksByReason = async ({
	skipReasonId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/skippedTasksByReason?skipReasonId=${skipReasonId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getStopsByReason = async ({
	stopReasonId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/stopsByReason?stopReasonId=${stopReasonId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getServiceStatusChangesByReason = async ({
	changeStatusReasonId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/serviceStatusChangesByReason?changeStatusReasonId=${changeStatusReasonId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelAssetsAvailable = async ({ modelId = "" }) => {
	try {
		let response = await API.get(
			`${Apis.ModelAssets}/available?modelId=${modelId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelVersionRoles = async ({ modelVersionId = "" }) => {
	try {
		let response = await API.get(
			`${Apis.ModelRoles}?modelVersionId=${modelVersionId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelVersionIntervals = async ({ modelVersionId = "" }) => {
	try {
		let response = await API.get(
			`${Apis.ModelIntervals}?modelVersionId=${modelVersionId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getConditionMonitoringQuestions = async ({
	modelVersionId = "",
	modelVersionRoleId = "",
	modelVersionIntervalId = "",
}) => {
	try {
		let response = await API.get(
			`${Apis.Analytics}/conditionmonitoringquestions?modelVersionId=${modelVersionId}&modelVersionRoleId=${modelVersionRoleId}&modelVersionIntervalId=${modelVersionIntervalId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getConditionMonitoring = async ({
	modelVersionTaskQuestionId = "",
	siteAssetId = "",
	modelVersionStageId = "",
	modelVersionZoneId = "",
	startDate = "",
	endDate = "",
}) => {
	try {
		let response = await API.get(
			`${Apis.Analytics}/conditionmonitoring?modelVersionTaskQuestionId=${modelVersionTaskQuestionId}&siteAssetId=${siteAssetId}&modelVersionStageId=${modelVersionStageId}&modelVersionZoneId=${modelVersionZoneId}&startDate=${startDate}&endDate=${endDate}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getCompletedVsOutstanding = async ({
	defectTypeId = "",
	startDate = "",
	endDate = "",
	defectRiskRatingId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	defectStatusType = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/completedvsoutstanding?defectTypeId=${defectTypeId}&startDate=${startDate}&endDate=${endDate}&defectRiskRatingId=${defectRiskRatingId}${deptQuery}&modelId=${modelId}&defectStatusType=${defectStatusType}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getAverageTimes = async ({
	modelVersionId = "",
	siteAssetId = "",
	modelVersionRoleId = "",
	modelVersionIntervalId = "",
	modelVersionStageId = "",
	modelVersionZoneId = "",
	startDate = "",
	endDate = "",
}) => {
	try {
		let response = await API.get(
			`${
				Apis.Analytics
			}/averagetimes?modelVersionId=${modelVersionId}&siteAssetId=${siteAssetId}&startDate=${startDate}&endDate=${endDate}${
				modelVersionRoleId && `&modelVersionRoleId=${modelVersionRoleId}`
			}&modelVersionIntervalId=${modelVersionIntervalId}&modelVersionStageId=${modelVersionStageId}&modelVersionZoneId=${modelVersionZoneId}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};


const getDefectsByStatus = async ({
	startDate = "",
	endDate = "",
	defectRiskRatingId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	defectStatusId = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsByStatus?startDate=${startDate}&endDate=${endDate}&defectRiskRatingId=${defectRiskRatingId}${deptQuery}&modelId=${modelId}&defectStatusId=${defectStatusId}`
      		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getDefectsByDepartment = async ({
	startDate = "",
	endDate = "",
	defectRiskRatingId = "",
	modelId = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/defectsByDepartment?startDate=${startDate}&endDate=${endDate}&defectRiskRatingId=${defectRiskRatingId}&modelId=${modelId}${deptQuery}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getCompletedServiceAnalysis = async ({
	siteAssetId = "",
	modelVersionId = "",
	modelVersionRoleId = "",
	modelVersionIntervalId = "",
	modelVersionStageId = "",
	modelVersionZoneId = "",
	startDate = "",
	endDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelId = "",
	serviceId = "",
	occurrence = "",
}) => {
	try {
		const deptQuery = buildDepartmentQuery(siteDepartmentId, siteDepartmentIds);
		let response = await API.get(
			`${Apis.Analytics}/completedServiceAnalysis?siteAssetId=${siteAssetId}&modelVersionId=${modelVersionId}&modelVersionRoleId=${modelVersionRoleId}&modelVersionIntervalId=${modelVersionIntervalId}&modelVersionStageId=${modelVersionStageId}&modelVersionZoneId=${modelVersionZoneId}&startDate=${startDate}&endDate=${endDate}${deptQuery}&modelId=${modelId}&serviceId=${serviceId}&occurrence=${occurrence}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const editConditionMonitoring = async (serviceTaskQuestionId, { valueNumeric, cmEditReasonID, otherReason }) => {
	try {
		let response = await API.post(
			`${Apis.Analytics}/conditionmonitoring/${serviceTaskQuestionId}`,
			{
				valueNumeric,
				cmEditReasonID,
				otherReason
			}
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export {
	getDefectRiskRatings,
	getDefectTypes,
	getSiteDepartments,
	getModelsPublished,
	getDefectsByType,
	getDefectsByDepartment,
	getDefectsByRiskRating,
	getDefectsBySystem,
	getDefectsRegistered,
	getMissingPartsTools,
	getOverdueServices,
	getPlannedWorkCompliance,
	getPausesByReason,
	getSkippedTasksByReason,
	getStopsByReason,
	getServiceStatusChangesByReason,
	getModelAssetsAvailable,
	getModelVersionRoles,
	getModelVersionIntervals,
	getConditionMonitoringQuestions,
	getConditionMonitoring,
	editConditionMonitoring,
	getCompletedVsOutstanding,
	getAverageTimes,
	getDefectsByStatus,
	getCompletedServiceAnalysis,
};
